import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { fetchAllUsersUsingProfileId, fetchPendingUsers } from '../UserManagementPage/UserManagementPage.duck';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities, denormalizeAssetData } from '../../util/data';
import { getManualMessages, conversations, manualTransactions, getOwnJobRfpListings } from '../../util/api';
import { updateTransactionMetadata } from '../../util/api';
import socket from '../../util/socket';
import { userTypeOf } from '../../util/userType';
import { teamRoleOf } from '../../util/teamRole';
import { showPostingListing } from '../../util/postingRead';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/DashboardPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/DashboardPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/DashboardPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/DashboardPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/DashboardPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/DashboardPage/FETCH_MESSAGES_ERROR';

export const MANUAL_FETCH_MESSAGES_REQUEST = 'app/DashboardPage/MANUAL_FETCH_MESSAGES_REQUEST';
export const MANUAL_FETCH_MESSAGES_SUCCESS = 'app/DashboardPage/MANUAL_FETCH_MESSAGES_SUCCESS';
export const MANUAL_FETCH_MESSAGES_ERROR = 'app/DashboardPage/MANUAL_FETCH_MESSAGES_ERROR';

export const GET_ALL_TRANSACTIONS_REQUEST = 'app/DashboardPage/GET_ALL_TRANSACTIONS_REQUEST';
export const GET_ALL_TRANSACTIONS_SUCCESS = 'app/DashboardPage/GET_ALL_TRANSACTIONS_SUCCESS';
export const GET_ALL_TRANSACTIONS_ERROR = 'app/DashboardPage/GET_ALL_TRANSACTIONS_ERROR';

export const SET_LOADING_STATE = 'app/DashboardPage/SET_LOADING_STATE';
export const SET_ALL_JOBS_APPLIED = 'app/DashboardPage/SET_ALL_JOBS_APPLIED';
export const SET_ALL_RFPS_APPLIED = 'app/DashboardPage/SET_ALL_RFPS_APPLIED';
export const SET_TOTAL_JOBS_POSTED = 'app/DashboardPage/SET_TOTAL_JOBS_POSTED';
export const SET_TOTAL_RFPS_POSTED = 'app/DashboardPage/SET_TOTAL_RFPS_POSTED';
export const SET_PROFILE_LISTING_ADMIN_ID = 'app/DashboardPage/SET_PROFILE_LISTING_ADMIN_ID';
export const DASHBOARD_UPDATE_LAST_MESSAGE = 'app/DashboardPage/DASHBOARD_UPDATE_LAST_MESSAGE';

// ================ Helper functions ================ //

/**
 * Ensures ongoingMessageFetches is always a valid array
 * Handles corrupted state from serialization issues
 */
const ensureOngoingFetchesIsArray = (fetches) => {
  // If it's already a valid array, return as is
  if (Array.isArray(fetches)) {
    return fetches;
  }
  
  // If it's a Set, convert to array
  if (fetches instanceof Set) {
    return Array.from(fetches);
  }
  
  // If it's iterable (like a corrupted Set object), try to convert
  if (fetches && typeof fetches[Symbol.iterator] === 'function') {
    try {
      return Array.from(fetches);
    } catch (e) {
      console.warn('Failed to convert iterable to array:', e);
      return [];
    }
  }
  
  // If it's an object with numeric keys (serialized array), try to convert
  if (fetches && typeof fetches === 'object') {
    const keys = Object.keys(fetches);
    if (keys.every(key => !isNaN(key))) {
      return Object.values(fetches);
    }
  }
  
  // Default to empty array for any other case
  return [];
};

/**
 * Adds a transaction ID to the ongoing fetches array if not already present
 */
const addToOngoingFetches = (ongoingFetches, txId) => {
  const safeArray = ensureOngoingFetchesIsArray(ongoingFetches);
  return txId && !safeArray.includes(txId) 
    ? [...safeArray, txId] 
    : safeArray;
};

/**
 * Removes a transaction ID from the ongoing fetches array
 */
const removeFromOngoingFetches = (ongoingFetches, txId) => {
  const safeArray = ensureOngoingFetchesIsArray(ongoingFetches);
  return safeArray.filter(id => id !== txId);
};

/**
 * Checks if a transaction ID is in the ongoing fetches array
 */
const isInOngoingFetches = (ongoingFetches, txId) => {
  const safeArray = ensureOngoingFetchesIsArray(ongoingFetches);
  return safeArray.includes(txId);
};

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// Improved merging function that handles different ID structures
const mergeEntityArrays = (a, b) => {
  const getId = (entity) => entity.id?.uuid || entity.id;
  
  const merged = a.filter(aEntity => !b.find(bEntity => getId(aEntity) === getId(bEntity))).concat(b);
  
  return merged;
};

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  users: [],
  pendingUsers: [],
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  initialMessageFailedToTransaction: null,
  messages: {},
  participants: {},
  // Use Array instead of Set for serialization compatibility
  ongoingMessageFetches: [],
  profileListingAdminId: null,
  loading: false,
  jobsApplied: null,
  rfpsApplied: null,
  totalJobsPosted: 0,
  totalRfpsPosted: 0,
};

export default function dashboardPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  // DEFENSIVE PROGRAMMING: Ensure state has valid ongoingMessageFetches array
  const safeState = {
    ...state,
    ongoingMessageFetches: ensureOngoingFetchesIsArray(state.ongoingMessageFetches)
  };

  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...safeState, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...safeState,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload);
      return { ...safeState, fetchInProgress: false, fetchOrdersOrSalesError: payload };
   
    case FETCH_MESSAGES_REQUEST: {
      const { txId } = payload || {};
      return { 
        ...safeState, 
        fetchMessagesInProgress: true, 
        fetchMessagesError: null,
        ongoingMessageFetches: addToOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }
    case FETCH_MESSAGES_SUCCESS: {
      const { txId, messages, participant } = payload;
      const existingMessages = safeState.messages[txId] || [];
      const mergedMessages = mergeEntityArrays(existingMessages, messages);
      
      return {
        ...safeState,
        fetchMessagesInProgress: false,
        messages: {
          ...safeState.messages,
          [txId]: mergedMessages,
        },
        participants: {
          ...safeState.participants,
          [txId]: participant,
        },
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched: Math.max(safeState.oldestMessagePageFetched, payload.page),
        ongoingMessageFetches: removeFromOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }
    case FETCH_MESSAGES_ERROR: {
      const { txId } = payload || {};
      
      return { 
        ...safeState, 
        fetchMessagesInProgress: false, 
        fetchMessagesError: payload,
        ongoingMessageFetches: removeFromOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }

    case MANUAL_FETCH_MESSAGES_REQUEST: {
      const { txId } = payload || {};
      return { 
        ...safeState, 
        fetchMessagesInProgress: true, 
        fetchMessagesError: null,
        ongoingMessageFetches: addToOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }
    case MANUAL_FETCH_MESSAGES_SUCCESS: {
      const { txId, messages, participant } = payload;
      const existingMessages = safeState.messages[txId] || [];
      const mergedMessages = mergeEntityArrays(existingMessages, messages);

      return {
        ...safeState,
        fetchMessagesInProgress: false,
        messages: {
          ...safeState.messages,
          [txId]: mergedMessages,
        },
        participants: {
          ...safeState.participants,
          [txId]: participant,
        },
        ongoingMessageFetches: removeFromOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }
    case MANUAL_FETCH_MESSAGES_ERROR: {
      const { txId } = payload || {};
      
      return { 
        ...safeState, 
        fetchMessagesInProgress: false, 
        fetchMessagesError: payload,
        ongoingMessageFetches: removeFromOngoingFetches(safeState.ongoingMessageFetches, txId)
      };
    }

    case SET_PROFILE_LISTING_ADMIN_ID: 
      return {
        ...safeState,
        profileListingAdminId: payload,
      };

    case GET_ALL_TRANSACTIONS_REQUEST:
      return { ...safeState, getAllTransactionsInProgress: true, getAllTransactionsError: null };
    case GET_ALL_TRANSACTIONS_SUCCESS:
      return {
        ...safeState,
        getAllTransactionsInProgress: false,
        jobsApplied: payload.jobsApplied,
        rfpsApplied: payload.rfpsApplied,
      };
    case GET_ALL_TRANSACTIONS_ERROR:
      console.error(payload);
      return { ...safeState, getAllTransactionsInProgress: false, getAllTransactionsError: payload };

    case SET_LOADING_STATE:
      return {
        ...safeState,
        loading: payload,
      };

    case SET_ALL_JOBS_APPLIED:
      return {
        ...safeState,
        jobsApplied: payload,
      };
    case SET_ALL_RFPS_APPLIED:
      return {
        ...safeState,
        rfpsApplied: payload,
      };

    
    case SET_TOTAL_JOBS_POSTED:
      return {
        ...safeState,
        totalJobsPosted: payload,
      };

    case SET_TOTAL_RFPS_POSTED:
      return {
        ...safeState,
        totalRfpsPosted: payload,
      };

    case DASHBOARD_UPDATE_LAST_MESSAGE: {
      const { conversationId, message, incrementUnread } = payload;
      if (!safeState.messages[conversationId]) return safeState;
      const existingParticipant = safeState.participants[conversationId];
      const newParticipant = incrementUnread && existingParticipant
        ? { ...existingParticipant, unreadCount: (existingParticipant.unreadCount || 0) + 1 }
        : existingParticipant;
      return {
        ...safeState,
        messages: { ...safeState.messages, [conversationId]: [message] },
        participants: newParticipant
          ? { ...safeState.participants, [conversationId]: newParticipant }
          : safeState.participants,
      };
    }

    default:
      return safeState;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchMessagesRequest = (txId) => ({ type: FETCH_MESSAGES_REQUEST, payload: { txId } });
const fetchMessagesSuccess = (txId, messages, pagination, participant) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { txId, messages, ...pagination, participant },
});
const fetchMessagesError = (e, txId) => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: { ...e, txId } });

const manualFetchMessagesRequest = (txId) => ({ type: MANUAL_FETCH_MESSAGES_REQUEST, payload: { txId } });
const manualFetchMessagesSuccess = (txId, messages, participant) => ({
  type: MANUAL_FETCH_MESSAGES_SUCCESS,
  payload: { txId, messages, participant },
});
const manualFetchMessagesError = (e, txId) => ({ type: MANUAL_FETCH_MESSAGES_ERROR, error: true, payload: { ...e, txId } });

export const getAllTransactionsRequest = () => ({ type: GET_ALL_TRANSACTIONS_REQUEST });
export const getAllTransactionsSuccess = (payload) => ({
  type: GET_ALL_TRANSACTIONS_SUCCESS,
  payload: payload,
});
export const getAllTransactionsError = (e) => ({
  type: GET_ALL_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

export const setLoadingState = (payload) => ({
  type: SET_LOADING_STATE,
  payload: payload,
});

export const setAllJobsApplied = (payload) => ({
  type: SET_ALL_JOBS_APPLIED,
  payload: payload,
});

export const setAllRfpsApplied = (payload) => ({
  type: SET_ALL_RFPS_APPLIED,
  payload: payload,
});

export const setTotalJobsPosted = (payload) => ({
  type: SET_TOTAL_JOBS_POSTED,
  payload: payload,
});

export const setTotalRfpsPosted = (payload) => ({
  type: SET_TOTAL_RFPS_POSTED,
  payload: payload,
});

export const dashboardUpdateLastMessage = (conversationId, message, incrementUnread = false) => ({
  type: DASHBOARD_UPDATE_LAST_MESSAGE,
  payload: { conversationId, message, incrementUnread },
});

const setProfileListingAdminId = (id) => ({
  type: SET_PROFILE_LISTING_ADMIN_ID,
  payload: id,
});

// ================ Helper Functions for Messages ================ //

const MESSAGES_PAGE_SIZE = 100;

const getMainAccountId = async (user, sdk) => {
  if (!user) return null;
  
  const userType = userTypeOf(user);
  const role = teamRoleOf(user);
  
  if (userType === 'agency' || userType === 'customer') {
    if (role === 'Admin' && user?.attributes?.profile?.publicData?.firstListingId !== undefined) {
      return user.id?.uuid;
    } else {
      const profileListingId = user.attributes?.profile?.publicData?.profileListingId;
      
      const listingResponse = await sdk.listings.show({ id: profileListingId });
      const listing = listingResponse?.data?.data;
      
      return listing?.attributes?.publicData?.adminId;
    }
  }
  
  return user.id?.uuid;
};

const getNormalisedMessages = (included, messageId) => {
  if(!included || !Array.isArray(included)) return;

  let message = included.find(msg => msg.type === 'message' && msg?.id?.uuid === messageId);

  let toSendMessage = {
    attributes: message?.attributes,
    id: message?.id,
    sender: message?.relationships?.sender?.data,
  };

  return toSendMessage || null;
};

const combineAndSortMessages = (manualMessages, denormalizedMessages) => {
  const denormMap = new Map(
    denormalizedMessages.map(msg => [msg.id.uuid, msg])
  );

  const combined = manualMessages.map(manualMsg => {
    const denormMsg = denormMap.get(manualMsg?.messageId);
    return denormMsg ? denormMsg : {
      id: { uuid: manualMsg?.messageId },
      attributes: {
        content: manualMsg?.message,
        createdAt: manualMsg?.createdAt
      },
      sender: {
        id: { uuid: manualMsg?.senderId },
        type: "user"
      },
      isManualMessage: true,
      receiver: {
        id: { uuid: manualMsg?.receiverId },
      },
    };
  });

  const manualIds = new Set(manualMessages.map(m => m.messageId));
  denormalizedMessages.forEach(msg => {
    if (!manualIds.has(msg.id.uuid)) {
      combined.push(msg);
    }
  });

  combined.sort((a, b) => new Date(a.attributes.createdAt) - new Date(b.attributes.createdAt));

  return combined;
}

const fetchMessages = (txId, page, config, tx) => async (dispatch, getState, sdk) => {
  const currentState = getState();
  
  // Use array-based check instead of Set.has()
  if (isInOngoingFetches(currentState.DashboardPage.ongoingMessageFetches, txId)) {
    return Promise.resolve();
  }
  
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest(txId));

  try {
    const response = await sdk.messages.query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...paging,
    });

    const messages = denormalisedResponseEntities(response);
    const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
    const pagination = { totalItems, totalPages, page: fetchedPage };

    const currentUserId = getState().user?.currentUser?.id?.uuid;
    const otherMsg = messages.find(m => m.sender?.id?.uuid !== currentUserId);
    let otherUserId = null;
    let otherUser = null;
    let otherUserProfileListingId = null;
    let otherUserProfileListing = null;

    if (otherMsg === undefined) {
      otherUserId = tx?.relationships?.provider?.data?.id?.uuid;
      if (otherUserId) {
        otherUser = await sdk.users.show({ id: otherUserId });
        const otherUserType = userTypeOf(otherUser?.data?.data);
        otherUserProfileListingId = otherUser?.data?.data?.attributes?.profile?.publicData?.firstListingId || otherUser?.data?.data?.attributes?.profile?.publicData?.profileListingId;
        
        if (otherUserProfileListingId) {
          otherUserProfileListing = await sdk.listings.show({ 
            id: otherUserProfileListingId, 
            include: ['images'],
            'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x'] 
          });
        }
      }
    }

    let participant = otherMsg
      ? {
          displayName: otherMsg?.sender?.attributes?.profile?.displayName || '',
          id: otherMsg.sender?.id?.uuid,
          bio: otherMsg?.sender?.attributes?.profile?.bio || '',
          agencyOrCustomerName:
            otherMsg?.sender?.attributes?.profile?.publicData?.agencyProfile?.name ||
            otherMsg?.sender?.attributes?.profile?.publicData?.customerProfile?.name || '',
          agencyDescription: otherMsg?.sender?.attributes?.profile?.publicData?.agencyProfile?.agencyDescription || '',
          customerDescription: otherMsg?.sender?.attributes?.profile?.publicData?.customerProfile?.customerDescription || '',
          userType: userTypeOf(otherMsg?.sender) || '',
          profileImage: otherMsg?.sender?.profileImage || null,
          profileImageId: otherMsg?.sender?.profileImage?.id?.uuid,
          profileListingId: otherMsg?.sender?.attributes?.profile?.publicData?.profileListingId || otherMsg?.sender?.attributes?.profile?.publicData?.firstListingId || null,
        }
      : {
          displayName: otherUser?.data?.data?.attributes?.profile?.displayName || '',
          id: otherUser?.data?.data?.id?.uuid,
          bio: otherUser?.data?.data?.attributes?.profile?.bio || '',
          agencyOrCustomerName:
            otherUser?.data?.data?.attributes?.profile?.publicData?.agencyProfile?.name ||
            otherUser?.data?.data?.attributes?.profile?.publicData?.customerProfile?.name || '',
          agencyDescription: otherUser?.data?.data?.attributes?.profile?.publicData?.agencyProfile?.agencyDescription || '',
          customerDescription: otherUser?.data?.data?.attributes?.profile?.publicData?.customerProfile?.customerDescription || '',
          userType: userTypeOf(otherUser?.data?.data) || '',
          profileImage: otherUserProfileListing?.data?.included?.[0] || null,
          profileImageId: otherUserProfileListing?.data?.included?.[0]?.id?.uuid,
          profileListingId: otherUserProfileListingId || null,
        };

    // const currentUserId = getState().user?.currentUser?.id?.uuid;
    if(currentUserId !== tx?.attributes?.metadata?.sentUserId) {
      participant.seenByReceivedUser = tx?.attributes?.metadata?.seenByReceivedUser;
    }

    dispatch(fetchMessagesSuccess(txId, messages, pagination, participant));

    if (page === 1 && totalPages > 1) {
      for (let nextPage = 2; nextPage <= totalPages; nextPage++) {
        try {
          await dispatch(fetchMessages(txId, nextPage, config, tx));
        } catch (e) {
          console.error(`Failed to fetch page ${nextPage} for transaction ${txId}`, e);
        }
      }
    }

    return response;
  } catch (e) {
    console.error(`Error fetching messages for transaction ${txId}:`, e);
    dispatch(fetchMessagesError(storableError(e), txId));
    throw e;
  }
};

export const getManualTransactions = async (sdk, dispatch, userId, userListingId, txType) => {
  dispatch(getAllTransactionsRequest());

  try {
    if (txType === 'message') {
      const convs = await conversations({ actionType: 'list', userId });
      return convs || [];
    }

    let txns = await manualTransactions({ userAId: userId, txType: txType, type:'fetch' });

    if (txType === 'apply' && userListingId) {
      let jobTxns = await manualTransactions({ agencyListingId: userListingId, txType: 'apply', type:'fetch' });
      txns = txns.concat(jobTxns);
    }

    // Deduplicate by transactionId (same txn can match both userAId and agencyListingId queries)
    const seen = new Set();
    txns = (txns || []).filter(t => {
      if (!t?.transactionId || seen.has(t.transactionId)) return false;
      seen.add(t.transactionId);
      return true;
    });

    return txns;
  } catch (error) {

    dispatch(getAllTransactionsError(storableError(error)));
    return null;
  }
};

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 100;

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const state = getState();
  const { currentUser } = state.user;
  const isCandidate = userTypeOf(currentUser) == 'candidate';
  const isCustomer = userTypeOf(currentUser) == 'customer';

  dispatch(setLoadingState(true));
  
  let promises = [];

  // if(!isCandidate) {
    if (!isCandidate) {
      promises = [
        ...promises,
        dispatch(fetchAllUsersUsingProfileId()),
        dispatch(fetchPendingUsers())
      ];
    }

    // Add messages fetching for dashboard
    try {
      const currentUserId = currentUser?.id?.uuid;
      let profileListingAdminId = null;

      const isAgencyOrCustomer = userTypeOf(currentUser) !== 'candidate';
      const role = teamRoleOf(currentUser);

      if(isAgencyOrCustomer) {
        profileListingAdminId = await getMainAccountId(currentUser, sdk);;
      } else {
        profileListingAdminId = currentUserId;
      }

      if (isCustomer) {
        // Read through the server, so the company's postings kept to its preferred network (held
        // back from the marketplace) are counted too; closed ones are left out, as before.
        let allListingsOfAdmin = await getOwnJobRfpListings({
          apiQueryParams: {
            authorId: profileListingAdminId,
            pub_listingType: ['job_listing', 'rfp_listing'],
            states: ['published', 'pendingApproval'],
          },
          responseFields: ['publicData'],
          publicDataFields: ['listingType', 'isDraftListing'],
        });
        allListingsOfAdmin = (allListingsOfAdmin?.allListings || []).flatMap(page => page?.data || []);

        if (allListingsOfAdmin?.length > 0) {
          const noOfJobListings = allListingsOfAdmin?.filter(listing => listing.attributes?.publicData?.listingType === 'job_listing' && !listing.attributes?.publicData?.isDraftListing)?.length || 0;
          const noOfRfpListings = allListingsOfAdmin?.filter(listing => listing.attributes?.publicData?.listingType === 'rfp_listing' && !listing.attributes?.publicData?.isDraftListing )?.length || 0;
          
          dispatch(setTotalJobsPosted(noOfJobListings));
          dispatch(setTotalRfpsPosted(noOfRfpListings));
        }
      }

      // Fetch all conversations
      const allMessageTransactionsResponse = await getManualTransactions(sdk, dispatch, profileListingAdminId, currentUser?.attributes?.profile?.publicData?.profileListingId, 'message');
      const allMessageTransactions = allMessageTransactionsResponse;

      if (allMessageTransactions && allMessageTransactions.length > 0) {
        const allConvs = allMessageTransactions;

        // Join socket rooms for real-time messages
        allConvs.forEach(conv => {
          if (conv?.conversationId) {
            socket.emit('join-conversation', conv.conversationId);
          }
        });

        const userCache = new Map();
        const profileListingCache = new Map();

        const getCachedUser = async (userId) => {
          if (userCache.has(userId)) return userCache.get(userId);
          const userResponse = await sdk.users.show({ id: userId });
          const user = userResponse?.data?.data;
          userCache.set(userId, user);
          return user;
        };

        const getCachedProfileListing = async (listingId) => {
          if (profileListingCache.has(listingId)) return profileListingCache.get(listingId);
          const listingResponse = await sdk.listings.show({
            id: listingId,
            include: ['images'],
            'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x'],
          });
          profileListingCache.set(listingId, listingResponse);
          return listingResponse;
        };

        const allPromises = allConvs.map(async (conv) => {
          try {
            const conversationId = conv?.conversationId;
            if (!conversationId) throw new Error('Conversation ID is missing');

            const otherParticipantId = (conv.participantIds || []).find(id => id !== profileListingAdminId);
            const neededUser = otherParticipantId ? await getCachedUser(otherParticipantId) : null;

            const neededUserProfileListingId =
              neededUser?.attributes?.profile?.publicData?.firstListingId ||
              neededUser?.attributes?.profile?.publicData?.profileListingId;

            let neededUserProfileListingResponse = null;
            if (neededUserProfileListingId) {
              neededUserProfileListingResponse = await getCachedProfileListing(neededUserProfileListingId);
            }

            const myConvParticipant = (conv.participants || []).find(p => p.userId === profileListingAdminId);
            const unreadCount = myConvParticipant?.unreadCount || 0;

            const participant = {
              displayName: neededUser?.attributes?.profile?.displayName || '',
              id: neededUser?.id?.uuid,
              bio: neededUser?.attributes?.profile?.bio || '',
              agencyOrCustomerName:
                neededUser?.attributes?.profile?.publicData?.agencyProfile?.name ||
                neededUser?.attributes?.profile?.publicData?.customerProfile?.name || '',
              agencyDescription:
                neededUser?.attributes?.profile?.publicData?.agencyProfile?.agencyDescription || '',
              customerDescription:
                neededUser?.attributes?.profile?.publicData?.customerProfile?.customerDescription || '',
              userType: userTypeOf(neededUser) || '',
              profileImage: neededUserProfileListingResponse?.data?.included?.[0] || null,
              profileImageId: neededUserProfileListingResponse?.data?.included?.[0]?.id?.uuid,
              profileListingId: neededUserProfileListingId || null,
              unreadCount,
            };

            // Use lastMessage snapshot from conversation doc — no extra DB fetch needed
            const lm = conv.lastMessage;
            const syntheticMsg = lm
              ? {
                  text: lm.type === 'text' ? (lm.textPreview || '') : '',
                  createdAt: lm.createdAt,
                  senderId: lm.senderId,
                  attachments: lm.type === 'image' || lm.type === 'file' ? [{ fileName: 'file' }] : [],
                }
              : null;

            dispatch(manualFetchMessagesSuccess(conversationId, syntheticMsg ? [syntheticMsg] : [], participant));

          } catch (error) {
            console.error(`Error processing conversation ${conv?.conversationId}:`, error);
            dispatch(manualFetchMessagesError(error));
          }
        });

        await Promise.allSettled(allPromises);

        if (profileListingAdminId !== null) {
          dispatch(setProfileListingAdminId(profileListingAdminId));
        }
      }

      if (!isCustomer) {
        const allApplyTxns = await getManualTransactions(sdk, dispatch, profileListingAdminId, currentUser?.attributes?.profile?.publicData?.profileListingId, 'apply');

        if (Array.isArray(allApplyTxns) && allApplyTxns.length > 0) {
          // Optimize: Combine all promises into a single array and execute in parallel
          const allFetchPromises = [];

          allApplyTxns.forEach(txn => {
            const txnListingId = txn?.listingId;
            const txnAgencyListingId = txn?.agencyListingId;
            const txnConsultantListingId = txn?.consultantListingId;

            if (txnListingId) {
              allFetchPromises.push(
                showPostingListing(sdk, { id: txnListingId })
                  .then(response => ({ type: 'listing', txn, data: response?.data?.data }))
                  .catch(error => {
                    console.error('Failed to fetch listing:', txnListingId, error);
                    return null;
                  })
              );
            }

            if (txnAgencyListingId) {
              allFetchPromises.push(
                sdk.listings.show({ id: txnAgencyListingId })
                  .then(response => ({ type: 'agencyListing', txn, data: response?.data?.data }))
                  .catch(error => {
                    console.error('Failed to fetch agency listing:', txnAgencyListingId, error);
                    return null;
                  })
              );
            }

            if (txnConsultantListingId) {
              allFetchPromises.push(
                sdk.listings.show({ id: txnConsultantListingId, 'fields.listing': ['title'] })
                  .then(response => ({ type: 'consultantListing', txn, data: response?.data?.data }))
                  .catch(error => {
                    console.error('Failed to fetch consultant listing:', txnConsultantListingId, error);
                    return null;
                  })
              );
            }
          });

          // Execute all fetches in parallel
          const allResults = await Promise.all(allFetchPromises);

          // Separate results by type
          const txnListings = allResults.filter(r => r?.type === 'listing').map(r => ({ txn: r.txn, listing: r.data }));
          const txnAgencyListings = allResults.filter(r => r?.type === 'agencyListing').map(r => ({ txn: r.txn, agencyListing: r.data }));
          const txnConsultantListings = allResults.filter(r => r?.type === 'consultantListing').map(r => ({ txn: r.txn, consultantListing: r.data }));

          // Create a map for quick lookup of agency listings by transaction ID
          const agencyListingMap = txnAgencyListings.reduce((acc, item) => {
            if (item?.txn?.transactionId && item?.agencyListing) {
              acc[item.txn.transactionId] = item.agencyListing;
            }
            return acc;
          }, {});
          const consultantListingMap = txnConsultantListings.reduce((acc, item) => {
            if (item?.txn?.transactionId && item?.consultantListing) {
              acc[item.txn.transactionId] = item.consultantListing;
            }
            return acc;
          }, {});

          // Categorize job and RFP listings
          const { jobs, rfps } = txnListings.reduce(
            (acc, item) => {
              if (!item?.listing) return acc;

              const { txn, listing } = item;
              const listingType = listing?.attributes?.publicData?.listingType;

              // Attach matching agency listing if available
              const agencyListing = agencyListingMap[txn?.transactionId] || null;
              const consultantListing = consultantListingMap[txn?.transactionId] || null;
              
              const entry = {
                txnId: txn?.transactionId,
                listing,
                listingType,
                agencyListing,
                consultantListing,
                status: txn?.status || 'pending',
                shortlistedOn: txn?.shortlistedAt || 'N/A',
                scheduledOn: txn?.scheduledAt || 'N/A',
                scheduledDate: txn?.scheduleDate || 'N/A',
                meetingLink: txn?.meetLink || 'N/A',
                eventId: txn?.eventId || 'N/A',
                rejectedOn: txn?.rejectedAt || 'N/A',
                meetingHeading: txn?.meetingHeading || 'N/A',
                meetingDescription: txn?.meetingDescription || 'N/A',
                appliedByAgency: txn?.appliedByAgency || false,
                pendingCandidateFirstName: txn?.pendingCandidateFirstName || '',
                pendingCandidateLastName: txn?.pendingCandidateLastName || '',
                pendingCandidateEmail: txn?.pendingCandidateEmail || '',
              };

              if (listingType === 'job_listing') acc.jobs.push(entry);
              else if (listingType === 'rfp_listing') acc.rfps.push(entry);

              return acc;
            },
            { jobs: [], rfps: [] }
          );

          dispatch(setAllJobsApplied(jobs));
          dispatch(setAllRfpsApplied(rfps));
        }
      }

    } catch(error) {
      console.error("Error in dashboard message fetching: ", error);
      dispatch(manualFetchMessagesError(error));
    }
  // }

  return Promise.all(promises)
    .then(response => {

      dispatch(setLoadingState(false));

      return response;
    })
    .catch(e => {
      // A rejection here (e.g. a cold/hard load where currentUser isn't yet in
      // the store, so profileListingId is undefined and sdk.listings.show
      // rejects) must still clear the loading flag — otherwise DashboardPage is
      // stranded on the LoadingSkeleton forever. The happy path clears it in the
      // .then above; this mirror keeps the failure path from stranding.
      dispatch(setLoadingState(false));
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.DashboardPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  if (!hasMoreOldMessages) {
    return Promise.resolve();
  }

  return dispatch(fetchMessages(txId, nextPage, config));
};

// ================ Additional Protection: Redux Middleware ================ //

/**
 * Middleware to validate and fix serialization issues in Redux state
 * This provides an additional layer of protection against corrupted state
 */
export const serializationValidationMiddleware = store => next => action => {
  const result = next(action);
  
  if (process.env.NODE_ENV === 'production') {
    const state = store.getState();
    
    // Check DashboardPage state specifically
    if (state.DashboardPage?.ongoingMessageFetches) {
      const fetches = state.DashboardPage.ongoingMessageFetches;
      
      // If it's not an array, log warning and fix it
      if (!Array.isArray(fetches)) {
        console.warn('[Redux Serialization Fix] Non-array ongoingMessageFetches detected:', typeof fetches);
        
        // Dispatch a fix action
        store.dispatch({
          type: 'app/DashboardPage/FIX_ONGOING_FETCHES',
          payload: ensureOngoingFetchesIsArray(fetches)
        });
      }
    }
  }
  
  return result;
};